// src/components/JarvislyModule/PageHeader/CardHeaderTitle.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useRef } from 'react';
import translateX from 'utils/translateX';
import { Tooltip, Input } from 'antd';
import { useModuleContext } from 'components/core/ModuleProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { baseTheme, rgba } from 'configs/appEngine';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************

export const CardHeaderTitle = ({
  name,
  tooltip,
  twoClickToEdit,
  fieldName = 'organization',
  placeholder,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // actionRefreshed comes from ModuleProvider directly
  const { actionRefreshed } = useModuleContext();
  // setAdditionalFields comes from SimpleFormContext (may be undefined if not inside SimpleFormProvider)
  const formContext = useSimpleFormContext();
  const setAdditionalFields = formContext?.setAdditionalFields;
  const { viewMode: mode } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(name || '');
  const [originalValue, setOriginalValue] = useState(name || '');
  const inputRef = useRef(null);
  const clickTimerRef = useRef(null);
  const clickCountRef = useRef(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Atualiza valores quando o name muda (data carregada ou atualizada via RefreshButton)
  useEffect(() => {
    setCurrentValue(name || '');
    setOriginalValue(name || '');
    // Limpa o additionalFields quando dados são recarregados
    if (setAdditionalFields) {
      setAdditionalFields(prev => {
        const { [fieldName]: removed, ...rest } = prev || {};
        return rest;
      });
    }
  }, [name, fieldName, setAdditionalFields]);

  // Quando RefreshButton é clicado, reverte para o valor original
  useEffect(() => {
    if (isEditing) {
      setIsEditing(false);
    }
    setCurrentValue(originalValue);
    // Limpa o additionalFields
    if (setAdditionalFields) {
      setAdditionalFields(prev => {
        const { [fieldName]: removed, ...rest } = prev || {};
        return rest;
      });
    }
  }, [actionRefreshed, originalValue, fieldName, setAdditionalFields]);

  // Foca o input quando entra em modo de edição
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDoubleClick = () => {
    if (!twoClickToEdit || mode !== 'edit') return;

    clickCountRef.current++;

    if (clickCountRef.current === 1) {
      // Primeiro clique - inicia timer
      clickTimerRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 300);
    } else if (clickCountRef.current === 2) {
      // Segundo clique - ativa edição
      clearTimeout(clickTimerRef.current);
      clickCountRef.current = 0;
      setIsEditing(true);
    }
  };

  const handleConfirmEdit = () => {
    const trimmedValue = currentValue.trim();

    if (!trimmedValue) {
      // Se vazio, reverte para o original
      setCurrentValue(originalValue);
      setIsEditing(false);
      return;
    }

    // Atualiza o valor localmente (visualmente)
    setCurrentValue(trimmedValue);
    setIsEditing(false);

    // Marca para ser salvo quando clicar em Save
    if (setAdditionalFields && trimmedValue !== originalValue) {
      setAdditionalFields(prev => ({
        ...prev,
        [fieldName]: trimmedValue,
      }));
    } else if (setAdditionalFields && trimmedValue === originalValue) {
      // Se voltou ao valor original, remove do additionalFields
      setAdditionalFields(prev => {
        const { [fieldName]: removed, ...rest } = prev || {};
        return rest;
      });
    }
  };

  const handleCancelEdit = () => {
    setCurrentValue(originalValue);
    setIsEditing(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirmEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleBlur = () => {
    handleConfirmEdit();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={currentValue}
        onChange={e => setCurrentValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          fontSize: '16px',
          fontWeight: '500',
          padding: '4px 11px',
          height: '32px',
          lineHeight: '1.5715',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          maxWidth: '400px',
          transition: 'all 0.2s',
          boxShadow: 'none',
        }}
        onFocus={e => {
          e.target.style.borderColor = baseTheme.colorPrimary;
          e.target.style.boxShadow = `0 0 0 2px ${rgba(baseTheme.colorPrimary, 0.2)}`;
        }}
        onBlurCapture={e => {
          e.target.style.borderColor = '#d9d9d9';
          e.target.style.boxShadow = 'none';
        }}
      />
    );
  }

  const titleContent = (
    <span
      onClick={handleDoubleClick}
      style={{
        cursor: twoClickToEdit && mode === 'edit' ? 'pointer' : 'default',
        userSelect: 'none',
        display: twoClickToEdit ? 'inline-block' : 'unset',
      }}
    >
      {currentValue || name || translateX(placeholder)}
    </span>
  );

  return tooltip ? (
    <Tooltip placement="top" title={translateX(tooltip)}>
      {titleContent}
    </Tooltip>
  ) : (
    titleContent
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CardHeaderTitle;

// *******************************************************************************
// HELPERS
// *******************************************************************************
